import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { scanError, useStartScan } from './site.js';
import { Button } from './ui.js';

/**
 * "Check the whole site again" (owner's request, 25.09): availability, SSL, domain, DNS, e-mail, the
 * home page, speed and – once ownership is confirmed – all pages and links, right now. Counts as one
 * manual scan of the plan's daily limit.
 */
export function FullRescan({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const scan = useStartScan(siteId, 'full');
  const result = scan.data;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          busy={scan.isPending}
          disabled={scan.isSuccess}
          onClick={() =>
            scan.mutate(undefined, {
              // Most checks finish within a minute; the crawl and speed test take a few more.
              onSuccess: () =>
                setTimeout(
                  () => void client.invalidateQueries({ queryKey: ['site', siteId] }),
                  30_000,
                ),
            })
          }
        >
          {t('rescan.button')}
        </Button>
        {scan.isIdle ? <span className="text-sm text-slate-700">{t('rescan.hint')}</span> : null}
      </div>
      {result ? (
        <p role="status" className="text-sm">
          {result.crawl === false ? (
            <>
              {t('rescan.queuedNoCrawl')}{' '}
              <Link to={`/sites/${siteId}/settings`} className="text-blue-800 underline">
                {t('rescan.verify')}
              </Link>
            </>
          ) : (
            t('rescan.queued')
          )}
        </p>
      ) : null}
      {scan.isError ? (
        <p role="alert" className="text-sm text-red-800">
          {scanError(scan.error, t)}
        </p>
      ) : null}
    </div>
  );
}
